import { useCallback, useEffect, useRef, useState } from 'react'

// Voice input via the browser's Web Speech API (Chrome, Edge, Safari). Not in
// TypeScript's DOM lib, so the few members used here are typed locally.
// Browsers without it (e.g. Firefox) report supported = false and the mic
// button is not shown. Note: Chrome and Edge send the audio to their own
// cloud speech service to transcribe it.

type SpeechRecognitionResultLike = {
    readonly length: number
    readonly [index: number]: { readonly transcript: string }
}

type SpeechRecognitionEventLike = {
    readonly results: {
        readonly length: number
        readonly [index: number]: SpeechRecognitionResultLike
    }
}

type SpeechRecognitionLike = {
    lang: string
    continuous: boolean
    interimResults: boolean
    onresult: ((event: SpeechRecognitionEventLike) => void) | null
    onerror: ((event: { error: string }) => void) | null
    onend: (() => void) | null
    start: () => void
    stop: () => void
    abort: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

const recognitionCtor = (): SpeechRecognitionCtor | null => {
    if (typeof window === 'undefined') return null
    const w = window as unknown as {
        SpeechRecognition?: SpeechRecognitionCtor
        webkitSpeechRecognition?: SpeechRecognitionCtor
    }
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

const ERROR_MESSAGES: Record<string, string> = {
    'not-allowed':
        'Microphone access is blocked. Allow it in your browser to use voice input.',
    'service-not-allowed':
        'Microphone access is blocked. Allow it in your browser to use voice input.',
    'audio-capture': 'No microphone was found.',
    'no-speech': 'Didn’t catch that. Tap the mic and try again.',
    network: 'Voice input needs an internet connection.',
}

/**
 * Dictation into the chat box. start(currentDraft) listens until the user
 * pauses (or stop() is called) and keeps calling onText with the draft plus
 * what has been heard so far, so the words appear as they are spoken. The text
 * is never sent automatically - the user reviews it and presses send.
 */
export const useSpeechInput = (onText: (text: string) => void) => {
    const [supported] = useState(() => recognitionCtor() !== null)
    const [listening, setListening] = useState(false)
    const [error, setError] = useState('')
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
    const onTextRef = useRef(onText)

    useEffect(() => {
        onTextRef.current = onText
    }, [onText])

    // Stop listening (and drop pending results) if the chat unmounts.
    useEffect(() => () => recognitionRef.current?.abort(), [])

    const start = useCallback((currentDraft: string) => {
        const Recognition = recognitionCtor()
        if (!Recognition || recognitionRef.current) return

        const recognition = new Recognition()
        recognition.lang = navigator.language || 'en-IN'
        recognition.continuous = false // ends after a pause
        recognition.interimResults = true

        const prefix = currentDraft.trim() ? `${currentDraft.trimEnd()} ` : ''
        recognition.onresult = (event) => {
            let heard = ''
            for (let i = 0; i < event.results.length; i++) {
                heard += event.results[i][0].transcript
            }
            onTextRef.current(prefix + heard)
        }
        recognition.onerror = (event) => {
            if (event.error === 'aborted') return
            setError(
                ERROR_MESSAGES[event.error] ??
                    'Voice input stopped unexpectedly. Please try again.',
            )
        }
        recognition.onend = () => {
            recognitionRef.current = null
            setListening(false)
        }

        setError('')
        recognitionRef.current = recognition
        try {
            recognition.start()
            setListening(true)
        } catch {
            recognitionRef.current = null
            setError('Voice input could not start. Please try again.')
        }
    }, [])

    /** Stop listening and keep what was heard. */
    const stop = useCallback(() => recognitionRef.current?.stop(), [])

    /** Stop listening and discard anything not yet delivered (send, close). */
    const cancel = useCallback(() => {
        recognitionRef.current?.abort()
        recognitionRef.current = null
        setListening(false)
    }, [])

    const clearError = useCallback(() => setError(''), [])

    return { supported, listening, error, start, stop, cancel, clearError }
}
